import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import type { Study } from '@/models/study';
import { formatStudyDate } from '@/shared/utils/studyDate';
import { Tag } from '@/shared/ui/Tag';
import {
  beginStudyLoadGesture,
  getReportNavigationGuard,
} from '@/features/study-detail';
import styles from './WorklistRow.module.css';

interface WorklistRowProps {
  study: Study;
  isActive: boolean;
  compact?: boolean;
}

export default function WorklistRow({
  study,
  isActive,
  compact = true,
}: WorklistRowProps) {
  const isStatPriority = study.priority === 'STAT';
  const isPending =
    study.assignedToMe && (study.reportStatus ?? '').toUpperCase() !== 'FINAL';
  const formattedDate = formatStudyDate(study.studyDate);
  const tags = study.tags ?? [];

  return (
    <li aria-current={isActive ? 'true' : undefined}>
      <Link
        to={`/studies/${study.uuid}`}
        className={styles.row}
        aria-selected={isActive}
        aria-current={isActive ? 'true' : undefined}
        // Paint the loading overlay in THIS frame, before the router's
        // startTransition defers everything: flushSync commits the pending
        // flag synchronously, then the navigation (and the old study's
        // teardown) proceed behind the already-visible loader. Mirror Link's
        // own gating: only a plain left-click navigates in-page — a modified
        // or non-primary click opens a new tab and must NOT start the gesture
        // (this page never navigates, so nothing would ever clear it).
        onClick={(e) => {
          if (isActive) return;
          if (
            e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey
          )
            return;
          // Unsaved changes with autosave off: the router blocker is about
          // to show Leave/Stay — nothing may move (no overlay, highlight,
          // breadcrumb, URL) unless the user confirms (AP-778 §7.1).
          if (getReportNavigationGuard()?.blocksNavigation) return;
          const patientName =
            `${study.patientFirstName ?? ''} ${study.patientLastName ?? ''}`.trim();
          flushSync(() =>
            beginStudyLoadGesture(study.uuid, patientName || undefined)
          );
        }}
      >
        <span
          className={`${styles.pendingDot} ${isPending ? styles.pendingDotVisible : ''}`}
          aria-hidden
        />
        <span
          className={`${styles.priorityBar} ${isStatPriority ? styles.priorityStat : ''}`}
          aria-hidden
        />
        <div className={styles.content}>
          <div className={styles.line1}>
            <span className={styles.name}>
              {study.patientFirstName} {study.patientLastName}
            </span>
            {study.priority && (
              <span
                className={styles.priorityPill}
                data-priority={study.priority}
              >
                {{ STAT: '▲', URGENT: '●', ROUTINE: '▼' }[study.priority] ?? ''}
                {!compact && ` ${study.priority}`}
              </span>
            )}
          </div>
          <div className={styles.line2}>
            <span className={styles.site}>{study.site ?? ''}</span>
            <span className={styles.separator}>&middot;</span>
            <span className={styles.date}>{formattedDate}</span>
            {study.reportStatus && (
              <span
                className={styles.statusPill}
                data-status={(study.reportStatus ?? '').toUpperCase()}
              >
                {study.reportStatus}
              </span>
            )}
          </div>
          {tags.length > 0 && (
            <div className={styles.line3}>
              {tags.map((tag) => (
                <Tag key={tag} value={tag} compact={compact} />
              ))}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}
